import { useQueryClient } from 'react-query';

import { useApiMutation, useApiQuery } from '../../api/useApi';
import type { EmailTemplate } from '../../pages/email-templates/api/useEmailTemplate';
import type { WhatsappTemplate } from '../../pages/settings/whatsapp/useWhatsappTemplate';

import { estRequeteConversations } from './useConversations';

/**
 * Les appels du composeur — #84.
 *
 * Hooks maison plutôt que ceux du web (`useSendMessage`, `useQuickMessages`,
 * `useWhatsappTemplates`), qui vivent tous dans `pages/` et sont donc hors
 * frontière (#23). Le motif est celui posé par #82 : quelques lignes sur
 * `useApiQuery` / `useApiMutation`, plutôt que d'extraire des hooks de fichiers du
 * web qu'on ne veut pas déplacer.
 *
 * **Rien ici n'est chargé à l'ouverture du fil.** Les modèles et les messages
 * rapides ne partent qu'à l'ouverture de leur feuille : #24 a fixé un délai
 * d'abandon court, et #31 a déjà ramené le fil de onze requêtes à quatre — ce
 * n'est pas pour que le composeur en rajoute au chargement.
 */

export interface MessageRapide {
    id: number;
    title: string;
    content: string;
}

/** Chargé À L'OUVERTURE de la feuille, jamais avant. */
export function useMessagesRapides(facilityId: number | null, actif: boolean, canal?: string) {
    const params = new URLSearchParams();
    if (facilityId !== null) params.set('facility_id', String(facilityId));
    if (canal) params.set('channel', canal);

    return useApiQuery<{ data: MessageRapide[] }>(
        actif && facilityId !== null ? `/quick-messages?${params.toString()}` : '',
        {},
        { staleTime: 5 * 60 * 1000 },
    );
}

/**
 * Les modèles WhatsApp approuvés de l'hôtel d'envoi.
 *
 * `for_messaging=true` exclut les modèles des établissements enfants — c'est le
 * paramètre que le web utilise pour la messagerie manuelle, et s'en écarter ferait
 * proposer des modèles d'un autre tenant. `facility_id` : l'hôtel d'envoi, jamais
 * le groupe sélectionné (retour Odomo 2026-09).
 */
export function useModelesWhatsapp(facilityId: number | null, actif: boolean) {
    return useApiQuery<WhatsappTemplate[]>(
        actif && facilityId !== null ? `/whatsapp-templates?for_messaging=true&facility_id=${facilityId}` : '',
        {},
        { staleTime: 5 * 60 * 1000 },
    );
}

/**
 * Les modèles d'e-mail de l'établissement — retour client 2026-09-25 : « pour
 * les e-mails, l'appli ne propose pas de types de message ». Même endpoint que
 * le web (`useEmailTemplates`, `pages/email-templates/api/useEmailTemplate.ts`),
 * sans filtre supplémentaire : `/email-templates` ne connaît pas d'équivalent au
 * `for_messaging` des modèles WhatsApp.
 *
 * Chargé À L'OUVERTURE de la feuille, jamais avant — même règle que les modèles
 * WhatsApp et les messages rapides.
 */
export function useModelesEmail(facilityId: number | null, actif: boolean) {
    return useApiQuery<EmailTemplate[]>(
        actif && facilityId !== null ? `/email-templates?facility_id=${facilityId}` : '',
        {},
        { staleTime: 5 * 60 * 1000 },
    );
}

export interface EnvoiMessage {
    customer_id: number;
    facility_id: number;
    type: 'email' | 'sms' | 'whatsapp' | 'live_chat';
    message?: string;
    body?: string;
    subject?: string;
    whatsapp_template_id?: number;
    /** Même champ que le web (`ChatSendingMessage.tsx`) — voir `MessageController::sendEmail`. */
    email_template_id?: number;
    locale?: string;
    reservation_id?: number;
    /** Une clé PAR MESSAGE, réutilisée à chaque réessai. Voir `useCleIdempotence`. */
    idempotency_key: string;
}

/**
 * L'envoi.
 *
 * **Synchrone** : #19 a corrigé la prémisse — `WhatsappService::send` est appelé en
 * ligne, la file de jobs ne sert qu'aux flows. Un refus de Meta est donc un 400
 * immédiat, portant son message brut. Il s'affiche ; il ne se perd pas.
 *
 * La clé d'idempotence est lue par le middleware `IdempotentRequest`, jamais par le
 * contrôleur (#70). Deux taps sur le même brouillon ne produisent qu'un message.
 */
export function useEnvoyerMessage(customerId: number | string) {
    const queryClient = useQueryClient();

    return useApiMutation<unknown, unknown, EnvoiMessage>('post', () => '/send-message', {
        onSuccess: () => {
            // Le fil d'abord — c'est lui qu'on regarde après avoir tapé « envoyer ».
            queryClient.invalidateQueries(`/get-messages/${customerId}`);
            queryClient.invalidateQueries({ predicate: estRequeteConversations });
        },
    });
}
